import React, { useRef, useState ,useContext,useEffect,useMemo} from "react";
import { GoogleMap, LoadScript, Autocomplete, Marker , InfoWindow, DirectionsService, DirectionsRenderer, Polyline} from "@react-google-maps/api";
import { locationContext } from '../layout/layout.js';
import { EVENT_TYPE_ICONS } from '../tripPlanTree/eventTypeIcons';
import { renderToStaticMarkup } from 'react-dom/server';
import RouteInfoWindow from '../../components/RouteInfoWindow';
import './myMap-container.css';
const EVENT_MARKER_SIZE = 34;
const DEFAULT_VIEWBOX = [0, 0, 24, 24];
const ALL_DAYS_LABEL = 'All Days';
const ALL_DAYS_FILTER = ALL_DAYS_LABEL;
const ICON_PIXEL_OFFSETS = {
  Location: { x: -2.2, y: -2.2 }
};

const parseViewBox = (viewBox) => {
  if (!viewBox || typeof viewBox !== 'string') {
    return DEFAULT_VIEWBOX;
  }

  const parts = viewBox.trim().split(/[ ,]+/).map(Number);
  if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
    return parts;
  }

  return DEFAULT_VIEWBOX;
};

function getMarkerIcon(eventType, options = {}) {
  const {
    size = EVENT_MARKER_SIZE,
    iconScale = 0.44,
    highlight = false,
    backgroundColor = highlight ? '#e3f2fd' : '#ffffff',
    borderColor = highlight ? '#1e88e5' : '#cfd8dc',
    shadowColor = highlight ? 'rgba(30, 136, 229, 0.45)' : 'rgba(33, 33, 33, 0.35)'
  } = options;

  const baseIconElement = EVENT_TYPE_ICONS[eventType] || EVENT_TYPE_ICONS.Other;
  const innerSize = size * iconScale;
  const viewBoxAttr = baseIconElement.props?.viewBox;
  const [minX, minY, vbWidth, vbHeight] = parseViewBox(viewBoxAttr);
  const dominantSize = Math.max(vbWidth, vbHeight) || 1;
  const scale = innerSize / dominantSize;
  const viewCenterX = minX + vbWidth / 2;
  const viewCenterY = minY + vbHeight / 2;
  const offsetPx = ICON_PIXEL_OFFSETS[eventType] || { x: 0, y: 0 };

  const sanitizedIcon = React.cloneElement(baseIconElement, {
    width: undefined,
    height: undefined,
    viewBox: viewBoxAttr || `${minX} ${minY} ${vbWidth} ${vbHeight}`,
    preserveAspectRatio: 'xMidYMid meet',
    style: {
      margin: 0,
      marginRight: 0,
      marginLeft: 0,
      filter: 'none',
      display: 'block'
    }
  });

  const markerSvg = renderToStaticMarkup(
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <filter id="markerShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor={shadowColor} floodOpacity="0.9" />
        </filter>
      </defs>
      <g filter="url(#markerShadow)">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={(size / 2) - 1}
          fill={backgroundColor}
          stroke={borderColor}
          strokeWidth="1.5"
        />
        <g transform={`translate(${size / 2} ${size / 2})`}>
          <g transform={`translate(${offsetPx.x || 0} ${offsetPx.y || 0})`}>
            <g transform={`scale(${scale})`}>
              <g transform={`translate(${-viewCenterX} ${-viewCenterY})`}>
                {sanitizedIcon}
              </g>
            </g>
          </g>
        </g>
      </g>
    </svg>
  );

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`;
}

const center = {
  lat: 0,
  lng: 0
};

const libraries = ["places", "geometry"]; // Define libraries outside the component
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Global counter for Google Places API calls
let placesApiCallCounter = 0;

// Function to increment and log the API call counter
const incrementPlacesApiCounter = () => {
  placesApiCallCounter++;
  console.log(`PLACE API CALL = ${placesApiCallCounter}`);
};

const MyMap = (props) => {
    // Remove local marker state, use tripLocations from props
    const { tripLocations = [], onAddMarker, selectedDayIdx, selectedEventIdx, trip , editingEvent, isMultiSelectMode, selectedEvents} = props;
    const [mapCenter, setMapCenter] = useState(center); // Initial map center
    const hasCenteredOnMarkers = useRef(false); // Track if we've already centered on markers
    const autocompleteRef = useRef(null);
    const {location,setLocation,addNodeCallback} = useContext(locationContext);
    const [updateMap,setUpdateMap] = useState(false);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [shouldAutoOpenInfoWindow, setShouldAutoOpenInfoWindow] = useState(false);
    const mapRef = useRef(null); // <-- Add mapRef here
    const [isBoundsMode, setIsBoundsMode] = useState(true);
    const [currentZoom, setCurrentZoom] = useState(10); // <-- Add currentZoom state
  const [isOwner, setIsOwner] = useState(true);
  const [useEventIcons, setUseEventIcons] = useState(true);
    
    // Filter dropdown state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedDayFilters, setSelectedDayFilters] = useState(new Set([ALL_DAYS_FILTER])); // Default to 'all'
    const filterDropdownRef = useRef(null);
    
    // Dynamic container style that adjusts for mobile
    const [isMobile, setIsMobile] = useState(false);
    // Close InfoWindow if selectedPlace is deleted (not in tripLocations)
  
    useEffect(() => {
      const mediaQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
      setIsMobile(mediaQuery.matches);
      
      const handleChange = (e) => {
        setIsMobile(e.matches);
      };
      mediaQuery.addListener(handleChange);
      
      return () => mediaQuery.removeListener(handleChange);
    }, []);
    
    const containerStyle = useMemo(() => {
      const height = isMobile ? '650px' : '650px';
      console.log('🗺️ Container style - isMobile:', isMobile, 'height:', height);
      return {
        width: '100%',
        height: height, // 50px less on mobile
        position: 'relative',
      };
    }, [isMobile]);
    
    // Route state
    const [directionsResponse, setDirectionsResponse] = useState(null);
    const [directionsService, setDirectionsService] = useState(null);
    const [routeInfoWindow, setRouteInfoWindow] = useState(null); // For route click info
    const [routeClickPosition, setRouteClickPosition] = useState(null);
    const [currentRouteRequest, setCurrentRouteRequest] = useState(null); // Store current route request for recalculation
    const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(-1); // Track selected route segment
    const [isHoveringRoute, setIsHoveringRoute] = useState(false); // Track if cursor is over route
    const [routeCleared, setRouteCleared] = useState(true); // Track if route was cleared
    
    // Debug: Track renders with optimization status
    const renderCount = useRef(0);
    renderCount.current += 1;
    console.log(`🔄 MyMap render #${renderCount.current} [OPTIMIZED]`, {
      directionsResponse: !!directionsResponse,
      isBoundsMode,
      locationName: location?.name,
      tripLocationsCount: tripLocations?.length,
      selectedDayIdx,
      selectedEventIdx    });

    // Check ownership on mount
    useEffect(() => {
        if (!props.trip || !props.trip._id) return;
        fetch(`${BACKEND_URL}/check-userownership/${props.trip._id}`, {
            method: 'GET',
            credentials: 'include'
        })
        .then(res => res.json())
        .then(data => setIsOwner(!!data.isOwner))
        .catch(() => setIsOwner(false));
    }, [props.trip]);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
                setShowFilterDropdown(false);
            }
        };

        if (showFilterDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilterDropdown]);

// Handle location updates when search text changes programmatically
useEffect(() => {
    // check if location null (although search text change) - this can happen when we select a route , in such case no need to do anything
    console.log('📍 Location useEffect triggered:', {
      location: location,
      isBoundsMode,
      hasValidCoords: !!(location && location.lat !== null && location.lng !== null),
      renderCount: renderCount.current
    });
    
    if (location && location.lat !== null && location.lng !== null && !isBoundsMode) {
    if (autocompleteRef.current) {
        const newLocation = {
            lat: location.lat,
            lng: location.lng,
        };
        
        console.log('📍 Setting map center and zoom:', newLocation, location.zoom);
        
        // Batch map updates using startTransition to reduce renders
        React.startTransition(() => {
          setMapCenter(newLocation);
        });
        
        mapRef.current?.setZoom(location.zoom); // set zoom so it details enough to see the location 
    }
  }
}, [location?.lat, location?.lng, location?.zoom, isBoundsMode]); // More specific dependencies to avoid unnecessary triggers
// handle location picking from the map
useEffect(() => {
    console.log('🎯 SelectedPlace useEffect triggered:', {
      selectedPlace: !!selectedPlace,
      selectedPlaceName: selectedPlace?.name,
      isBoundsMode,
      renderCount: renderCount.current
    });
    
    if (selectedPlace) {
      setLocation({
        ...location,
        name: selectedPlace.name,
        lat: selectedPlace.geometry?.location?.lat
          ? (typeof selectedPlace.geometry.location.lat === 'function'
              ? selectedPlace.geometry.location.lat()
              : selectedPlace.geometry.location.lat)
          : selectedPlace.lat,
        lng: selectedPlace.geometry?.location?.lng
          ? (typeof selectedPlace.geometry.location.lng === 'function'
              ? selectedPlace.geometry.location.lng()
              : selectedPlace.geometry.location.lng)
          : selectedPlace.lng,
        formatted_address: selectedPlace.formatted_address,
        place_id: selectedPlace.place_id,
        website: selectedPlace.website,
        url: selectedPlace.url,
        formatted_phone_number: selectedPlace.formatted_phone_number,
        rating: selectedPlace.rating,
        user_ratings_total: selectedPlace.user_ratings_total,
        weekday_text: selectedPlace.weekday_text,
        zoom: selectedPlace.zoom || location.zoom
      });
      if (isBoundsMode) setIsBoundsMode(false); // Reset bounds mode if we have a selected place
    }
}, [selectedPlace]);   

// Handle route segment selection for highlighting
const handleSegmentSelect = (segmentIndex) => {
  setSelectedSegmentIndex(segmentIndex);
};

// Handle travel mode change and recalculate route
const handleTravelModeChange = (newTravelMode) => {
  if (currentRouteRequest && window.google && window.google.maps) {
    const service = new window.google.maps.DirectionsService();
    
    // Create new request with updated travel mode
    const updatedRequest = {
      ...currentRouteRequest,
      travelMode: window.google.maps.TravelMode[newTravelMode]
    };
    
    // Update stored request
    setCurrentRouteRequest(updatedRequest);
    
    service.route(updatedRequest, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        console.log('Route recalculated successfully with travel mode:', newTravelMode);
        setDirectionsResponse(result);
        
        // Update the route info window with new data
        if (routeInfoWindow && routeClickPosition) {
          const route = result.routes[0];
          const leg = route.legs[0];
          
          const updatedRouteInfo = {
            distance: route.legs.reduce((total, leg) => total + leg.distance.value, 0),
            duration: route.legs.reduce((total, leg) => total + leg.duration.value, 0),
            distanceText: directionsResponse.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0) > 1000 
              ? (directionsResponse.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000).toFixed(1) + ' km'
              : directionsResponse.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0) + ' m',
            durationText: Math.round(directionsResponse.routes[0].legs.reduce((total, leg) => total + leg.duration.value, 0) / 60) + ' min',
            startAddress: leg.start_address,
            endAddress: route.legs[route.legs.length - 1].end_address,
            travelMode: newTravelMode,
            legs: route.legs // Include all legs for detailed breakdown
          };
          
          setRouteInfoWindow(updatedRouteInfo);
        }
      } else {
        console.error('Route recalculation failed due to ' + status);
        alert(`Could not calculate route for ${newTravelMode.toLowerCase()} mode. Please try a different travel mode.`);
      }
    });
  }
};

// Register the search callback  from parent (TripPlanTree)
useEffect(() => {
  console.log('🔗 Search callback useEffect triggered:', {
    hasCallback: !!props.registerSearchMapCallback,
    renderCount: renderCount.current
  });
  
  if (props.registerSearchMapCallback) {
    props.registerSearchMapCallback((name, arg1, arg2, arg3, triggerSearch = false,routePoints = null) => {
      console.log('� Search callback invoked:', {
        name,
        hasRoutePoints: !!(routePoints && routePoints.length >= 2),
        routePointsLength: routePoints?.length,
        triggerSearch,
        renderCount: renderCount.current
      });
      
      if (routePoints && routePoints.length >= 2) {
        // Handle route points - create directions
        console.log('Creating route with points:', routePoints);
        
        // Clear any existing route
        setDirectionsResponse(null);
        setCurrentRouteRequest(null); // Clear previous route request;
        
        // Create DirectionsService if not exists
        if (window.google && window.google.maps) {
          const service = new window.google.maps.DirectionsService();
          
          // Prepare waypoints (all points except first and last)
          const waypoints = routePoints.slice(1, -1).map(point => ({
            location: { lat: point.lat, lng: point.lng },
            stopover: true
          }));
          
          const request = {
            origin: { lat: routePoints[0].lat, lng: routePoints[0].lng },
            destination: { lat: routePoints[routePoints.length - 1].lat, lng: routePoints[routePoints.length - 1].lng },
            waypoints: waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false // Keep the order as specified
          };
          
          // Store the request for later recalculation
          setCurrentRouteRequest(request);
          
          service.route(request, (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              console.log('Route created successfully');
              
              // Batch route creation state updates using startTransition
              React.startTransition(() => {
                setDirectionsResponse(result);
                setIsBoundsMode(true); // Let the route fit the view
                setRouteCleared(false); // Reset route cleared state
              });
            } else {
              console.error('Directions request failed due to ' + status);
              alert('Could not create route. Please check that all locations are accessible by road.');
            }
          });
        }
      } else {
    
        // Clear any existing route when routePoints is null or insufficient
        console.log('🧹 Clearing route - routePoints:', routePoints, 'renderCount:', renderCount.current);

        // Batch all route clearing state updates using startTransition to reduce renders
        React.startTransition(() => {
          setDirectionsResponse(null);
          setCurrentRouteRequest(null); // Clear stored route request
          setRouteInfoWindow(null); // Clear route info window
          setSelectedSegmentIndex(-1); // Clear selected segment
          setRouteClickPosition(null);
          setRouteCleared(true); // Set route cleared state
        });
      if (!triggerSearch) {
      if ((Array.isArray(arg1) || (arg1 == 0 && arg2 == 0)) && mapRef.current) { // bound in multi location or if no map location (set for event)
        console.log('🎯 Setting bounds mode for multi-location');
        const sw = { lat: arg1[0][0], lng: arg1[0][1] };
        const ne = { lat: arg1[1][0], lng: arg1[1][1] };
        
        // Pad the bounds to zoom out more to show surrounding areas
        const padLat = (ne.lat - sw.lat) * -0.2; // 30% negative padding for more zoom out
        const padLng = (ne.lng - sw.lng) * -0.2;
        const paddedSw = { lat: sw.lat + padLat, lng: sw.lng + padLng };
        const paddedNe = { lat: ne.lat - padLat, lng: ne.lng - padLng };
        const paddedBounds = new window.google.maps.LatLngBounds(paddedSw, paddedNe);
        setTimeout(() => {
          if (mapRef.current) {
            window.google.maps.event.trigger(mapRef.current, 'resize');
            mapRef.current.fitBounds(paddedBounds);
          }
        }, 200);
        setIsBoundsMode(true);
       // setLocation(loc => ({ ...loc, name }));
      } else {
        console.log('🎯 Setting single location mode:', name, arg1, arg2, arg3);
        
        // Batch location updates using startTransition
        React.startTransition(() => {
          setIsBoundsMode(false);
          setLocation(loc => ({
            ...loc,
            name,
            lat: arg1 !== undefined ? arg1 : loc.lat,
            lng: arg2 !== undefined ? arg2 : loc.lng,
            zoom: arg3 !== undefined ? arg3 : loc.zoom
          }));
        });
        
        // Use setTimeout to ensure map operations happen after bounds mode is cleared
        setTimeout(() => {
          if (arg1 && arg2) {
            setMapCenter({ lat: arg1, lng: arg2 });
          }
          if (arg3 !== undefined && mapRef.current) {
            mapRef.current.setZoom(arg3);
          }
        }, 50); // Small delay to ensure state updates complete
      }
    } 
      setTimeout(() => {
        const input = document.querySelector('.search-bar');
        if (input) {
          input.value = name;
          if (triggerSearch) {
            input.focus();
            const event = new Event('input', { bubbles: true });
            input.dispatchEvent(event);
            const enterEvent = new KeyboardEvent('keydown', { key: 'Enter', bubbles: true });
            input.dispatchEvent(enterEvent);
          }
        }
      }, 100); 
  }});
  }
}, [props.registerSearchMapCallback, setLocation, routeCleared]);

// Register the clear marker function

useEffect(() => {
  if (props.setClearTempMarkerCallback) {
    props.setClearTempMarkerCallback(() => {
      setSelectedPlace(null);
      setShouldAutoOpenInfoWindow(false);
      // Also clear any route when clearing markers
      setDirectionsResponse(null);
      setCurrentRouteRequest(null); // Clear stored route request
      setRouteInfoWindow(null); // Clear route info window
      setSelectedSegmentIndex(-1); // Clear selected segment
      setRouteClickPosition(null);
    });
  }
}, [props.setClearTempMarkerCallback]);

// Register the clear selected place function for event deletion
useEffect(() => {
  if (props.setClearSelectedPlaceCallback) {
    console.log('✅ MyMap: Registering clearSelectedPlace function');
    props.setClearSelectedPlaceCallback(() => {
      console.log('🧹 MyMap: clearSelectedPlace function called');
      setSelectedPlace(null);
      setShouldAutoOpenInfoWindow(false);
    });
  } else {
    console.log('❌ MyMap: No setClearSelectedPlaceCallback prop received');
  }
}, [props.setClearSelectedPlaceCallback]);

// Fit map bounds when route is loaded
useEffect(() => {
  console.log('🗺️ Route bounds useEffect triggered:', {
    hasDirectionsResponse: !!directionsResponse,
    hasRoute: !!(directionsResponse && directionsResponse.routes && directionsResponse.routes[0]),
    isBoundsMode,
    hasMapRef: !!mapRef.current,
    renderCount: renderCount.current
  });
  
  if (directionsResponse && directionsResponse.routes && directionsResponse.routes[0] && isBoundsMode && mapRef.current) {
    console.log('🗺️ Fitting map bounds to route');
    const route = directionsResponse.routes[0];
    const bounds = new window.google.maps.LatLngBounds();
    setRouteCleared(false); // Reset route cleared state
    // Add all route points to bounds
    route.legs.forEach(leg => {
      bounds.extend(leg.start_location);
      bounds.extend(leg.end_location);
      leg.steps.forEach(step => {
        bounds.extend(step.start_location);
        bounds.extend(step.end_location);
      });
    });
    
    // Fit the map to show the route with some padding
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.fitBounds(bounds, { 
          top: 50, 
          right: 50, 
          bottom: 50, 
          left: 50 
        });
      }
    }, 100);
  }
}, [directionsResponse]);


  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      const zoom = 15;
      const name = place.name ? place.name : (place.formatted_address ? place.formatted_address.split(",")[0] : "");
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        
        // Fix for bounds mode transition
        if (isBoundsMode && mapRef.current) {
          setIsBoundsMode(false);
          // Force map resize and center/zoom after bounds mode
          setTimeout(() => {
            if (mapRef.current) {
              window.google.maps.event.trigger(mapRef.current, 'resize');
              mapRef.current.setCenter({ lat, lng });
              mapRef.current.setZoom(zoom);
            }
          }, 100);
        }
        
        setLocation({
          name,
          lat,
          lng,
          zoom,
          formatted_address: place.formatted_address,
          place_id: place.place_id || null,
          website: place.website || null,
          url: place.url || (place.place_id ? `https://www.google.com/maps/place/?q=place_id:${place.place_id}` : null),
          formatted_phone_number: place.formatted_phone_number || null,
          rating: place.rating || null,
          user_ratings_total: place.user_ratings_total || null,
          weekday_text: place.opening_hours?.weekday_text || [],
        });
        setSelectedPlace({
          name,
          geometry: place.geometry,
          zoom,
          formatted_address: place.formatted_address,
          place_id: place.place_id || null,
          website: place.website || null,
          url: place.url || (place.place_id ? `https://www.google.com/maps/place/?q=place_id:${place.place_id}` : null),
          formatted_phone_number: place.formatted_phone_number || null,
          rating: place.rating || null,
          user_ratings_total: place.user_ratings_total || null,
          weekday_text: place.opening_hours?.weekday_text || [],
        });
        setMapCenter({ lat, lng });
        if (onAddMarker) {
          onAddMarker({
            lat,
            lng,
            name,
            zoom,
            formatted_address: place.formatted_address,
            place_id: place.place_id || null,
            website: place.website || null,
            url: place.url || (place.place_id ? `https://www.google.com/maps/place/?q=place_id:${place.place_id}` : null),
            formatted_phone_number: place.formatted_phone_number || null,
            rating: place.rating || null,
            user_ratings_total: place.user_ratings_total || null,
            weekday_text: place.opening_hours?.weekday_text || [],
          });
        }
        setShouldAutoOpenInfoWindow(true);
      } else if (place && place.formatted_address) {
        // Fallback: Use Geocoder if geometry is missing
        const geocoder = new window.google.maps.Geocoder();
        incrementPlacesApiCounter('geocoding'); // Track Geocoding API call
        geocoder.geocode({ address: place.formatted_address }, (results, status) => {
          if (status === "OK" && results[0] && results[0].geometry && results[0].geometry.location) {
            const loc = results[0].geometry.location;
            setLocation({
              name,
              lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
              lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
              zoom,
              formatted_address: results[0].formatted_address,
              place_id: results[0].place_id || null,
              website: null,
              url: results[0].place_id ? `https://www.google.com/maps/place/?q=place_id:${results[0].place_id}` : null,
              formatted_phone_number: null,
              rating: null,
              user_ratings_total: null,
              weekday_text: [],
            });
            setSelectedPlace({
              name,
              geometry: results[0].geometry,
              zoom,
              formatted_address: results[0].formatted_address,
              place_id: results[0].place_id || null,
              website: null,
              url: results[0].place_id ? `https://www.google.com/maps/place/?q=place_id:${results[0].place_id}` : null,
              formatted_phone_number: null,
              rating: null,
              user_ratings_total: null,
              weekday_text: [],
            });
            if (onAddMarker) {
              onAddMarker({
                lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
                lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
                name,
                zoom,
                formatted_address: results[0].formatted_address,
                place_id: results[0].place_id || null,
                website: null,
                url: results[0].place_id ? `https://www.google.com/maps/place/?q=place_id:${results[0].place_id}` : null,
                formatted_phone_number: null,
                rating: null,
                user_ratings_total: null,
                weekday_text: [],
              });
            }
            setShouldAutoOpenInfoWindow(true);
          } else {
            alert("Could not find a valid location for your input. Please select a suggestion from the dropdown.");
          }
        });
      } else {
        alert("Could not find a valid location for your input. Please select a suggestion from the dropdown.");
      }
    }
  };
  
  const handlesetLocation = () => {
    if (addNodeCallback && selectedPlace && mapRef.current) {
      // Use currentZoom state instead of mapRef.current.getZoom()
      const zoom = currentZoom;
      const loc = selectedPlace.geometry.location;
      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
      addNodeCallback(
        selectedPlace.name,
        lat,
        lng,
        zoom,
        selectedPlace.rating,
        selectedPlace.user_ratings_total,
        selectedPlace.url,
        selectedPlace.formatted_address,
        selectedPlace.formatted_phone_number,
        selectedPlace.place_id
      );
      setMapCenter({ lat, lng });
    } else {
      console.log("addNodeCallback or mapRef or selectedPlace is not available.");
    }
};


/*
// Memoize selected marker calculation to prevent unnecessary recalculations
const selectedMarkerInfo = useMemo(() => {
  console.log('🎯 Calculating selectedMarkerInfo:', {
    selectedDayIdx,
    selectedEventIdx,
    hasTrip: !!trip,
    hasDays: !!(trip && trip.days),
    renderCount: renderCount.current
  });
  
  if (
    typeof selectedDayIdx === 'number' &&
    typeof selectedEventIdx === 'number' &&
    trip &&
    trip.days &&
    trip.days[selectedDayIdx] &&
    trip.days[selectedDayIdx].events &&
    trip.days[selectedDayIdx].events[selectedEventIdx]
  ) {
    const event = trip.days[selectedDayIdx].events[selectedEventIdx];
    if (
      event.location &&
      typeof event.location.lat === 'number' &&
      typeof event.location.lng === 'number'
    ) {
      return {
        lat: event.location.lat,
        lng: event.location.lng
      };
    }
  }
  return { lat: null, lng: null };
}, [selectedDayIdx, selectedEventIdx, trip]);
*/
function setNewplace(place,clickedLocation,placeName) 
{
  // Fix for bounds mode transition when clicking on map
  if (isBoundsMode && mapRef.current) {
    setIsBoundsMode(false);
    // Force map resize and center after bounds mode
    setTimeout(() => {
      if (mapRef.current) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
        mapRef.current.setCenter(clickedLocation);
        mapRef.current.setZoom(15);
      }
    }, 100);
  }
  
  setLocation({
                name: placeName,
                lat: clickedLocation.lat,
                lng: clickedLocation.lng,
                formatted_address: place.formatted_address,
                place_id: place.place_id || null,
                website: place.website || null,
                url: place.url || '', // Google-provided URL
                rating: place.rating || null,
                user_ratings_total: place.user_ratings_total || null,
                weekday_text: place.opening_hours?.weekday_text || [],
                formatted_phone_number: place.formatted_phone_number || null
              });
              
              if (onAddMarker) {
                onAddMarker({
                  lat: clickedLocation.lat,
                  lng: clickedLocation.lng,
                  name: placeName,
                  formatted_address: place.formatted_address,
                  place_id: place.place_id || null,
                  website: place.website || null,
                url: place.url || '', // Google-provided URL
                rating: place.rating || null,
                user_ratings_total: place.user_ratings_total || null,
                weekday_text: place.opening_hours?.weekday_text || [],
                formatted_phone_number: place.formatted_phone_number || null
                });
              }
              const placeGeometry = place.geometry || { location: { lat: clickedLocation.lat, lng: clickedLocation.lng } };
            setSelectedPlace({
                name: placeName,
                geometry: placeGeometry,
                formatted_address: place.formatted_address,
                place_id: place.place_id || null,
                  website: place.website || null,
                url: place.url || '', // Google-provided URL
                rating: place.rating || null,
                user_ratings_total: place.user_ratings_total || null,
                weekday_text: place.opening_hours?.weekday_text || [],
                formatted_phone_number: place.formatted_phone_number || null,
              });
              setShouldAutoOpenInfoWindow(true); // Ensure InfoWindow opens
              setMapCenter(clickedLocation);
}

// Handle route click to show route information
const handleRouteClick = (e) => {
  if (directionsResponse && e.latLng) {
    const clickPosition = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    
    // Extract route information from directionsResponse
    const route = directionsResponse.routes[0];
    const leg = route.legs[0]; // For simplicity, showing first leg info
    
    const routeInfo = {
      distance: route.legs.reduce((total, leg) => total + leg.distance.value, 0),
      duration: route.legs.reduce((total, leg) => total + leg.duration.value, 0),
      distanceText: route.legs.reduce((total, leg) => total + leg.distance.value, 0) > 1000 
        ? (route.legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000).toFixed(1) + ' km'
        : route.legs.reduce((total, leg) => total + leg.distance.value, 0) + ' m',
      durationText: Math.round(route.legs.reduce((total, leg) => total + leg.duration.value, 0) / 60) + ' min',
      startAddress: leg.start_address,
      endAddress: route.legs[route.legs.length - 1].end_address,
      travelMode: directionsResponse.request.travelMode,
      legs: route.legs // Include all legs for detailed breakdown
    };
    
    setRouteInfoWindow(routeInfo);
    setRouteClickPosition(clickPosition);
    
  }
};

  const isFilterActive = !selectedDayFilters.has(ALL_DAYS_FILTER);

  return (
    
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
  <div className={`myMap-container ${isHoveringRoute ? 'route-hover' : ''}`}>
    {/* Search Field and Filter Row */}
    <div style={{ 
      display: 'flex', 
      gap: '0',
      alignItems: 'center',
      position: 'relative',
      marginBottom: '8px'
    }}>
      {/* Search Field - 80% */}
      <div style={{ width: '85%' }}>
        <Autocomplete
          onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
          onPlaceChanged={handlePlaceChanged}
        >
          <input
            type="text"
            value={location.name || ''}
            className="search-bar"
            style={{ 
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
            onChange={(e) => {
              setLocation({ ...location, name: e.target.value })
            }}
          />
        </Autocomplete>
      </div>
      
      {/* 2% Margin */}
      <div style={{ width: '2%' }}></div>
      
      {/* Filter Dropdown Button + Icon Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <div style={{ width: 'auto', position: 'relative' }} ref={filterDropdownRef}>
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            aria-pressed={isFilterActive}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '0' : '6px',
              padding: '8px 12px',
              border: isFilterActive ? '1px solid #90caf9' : '1px solid #ccc',
              borderRadius: '4px',
              background: isFilterActive ? '#e3f2fd' : 'white',
              cursor: 'pointer',
              fontSize: '14px',
              width: 'fit-content',
              justifyContent: isMobile ? 'center' : 'space-between',
              boxSizing: 'border-box',
              boxShadow: isFilterActive ? '0 0 0 1px rgba(25,118,210,0.2)' : 'none',
              color: isFilterActive ? '#0d47a1' : '#555'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#555' }}>
              <svg width="12" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"></polygon>
              </svg>
            </div>
            
          </button>
          
          {/* Filter Dropdown */}
          {showFilterDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              minWidth: '150px',
              width: 'auto',
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
            {/* All option */}
            <div
              style={{
                padding: '2px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                background: selectedDayFilters.has(ALL_DAYS_FILTER) ? '#e3f2fd' : 'white',
                alignContent: 'left',
                }}
              onClick={() => {
                if (selectedDayFilters.has(ALL_DAYS_FILTER)) {
                  // If all is selected, deselect it and select nothing (which will show nothing)
                  setSelectedDayFilters(new Set());
                } else {
                  // Select all and deselect individual days
                  setSelectedDayFilters(new Set([ALL_DAYS_FILTER]));
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedDayFilters.has(ALL_DAYS_FILTER)}
                onChange={() => {}} // Handled by onClick above
                style={{  marginRight: '8px', display: 'inline-block', width: '16px'}}
              />
              <label 
              style={{ 
                display: 'inline-block', 
                overflow: 'clip',
                width: 'calc(100% - 32px)',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
               
              }}>{ALL_DAYS_LABEL}</label>
            </div>
            
            {/* Individual day options */}
            {trip && trip.days && trip.days.map((day, dayIdx) => (
              <div
                key={dayIdx}
                style={{
                  padding: '2px',
                  cursor: 'pointer',
                  borderBottom: dayIdx < trip.days.length - 1 ? '1px solid #eee' : 'none',
                  background: selectedDayFilters.has(dayIdx) ? '#e3f2fd' : 'white',
                  display: 'block',
                  textAlign: 'left',
                  lineHeight: 'normal'
                }}
                onClick={() => {
                  const newFilters = new Set(selectedDayFilters);
                  
                  if (newFilters.has(ALL_DAYS_FILTER)) {
                    // If all was selected, deselect it and select this day
                    newFilters.delete(ALL_DAYS_FILTER);
                    newFilters.add(dayIdx);
                  } else if (newFilters.has(dayIdx)) {
                    // Deselect this day
                    newFilters.delete(dayIdx);
                  } else {
                    // Select this day
                    newFilters.add(dayIdx);
                  }
                  
                  setSelectedDayFilters(newFilters);
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedDayFilters.has(dayIdx)}
                  onChange={() => {}} // Handled by onClick above
                  style={{ marginRight: '8px', display: 'inline-block', width: '16px' }}
                />
                <label style={{ 
                  display: 'inline-block', 
                  width: 'calc(100% - 32px)',
                  overflow: 'clip',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>{day.description || `Day ${dayIdx + 1}`}</label>
              </div>
            ))}
            </div>
          )}
        </div>

        <button
          onClick={() => setUseEventIcons(prev => !prev)}
          aria-pressed={useEventIcons}
          style={{
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: useEventIcons ? '#e3f2fd' : 'white',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#333'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 21s6-5.686 6-10A6 6 0 0 0 6 11c0 4.314 6 10 6 10z" />
          </svg>
        </button>
      </div>
      
      {/* Final 5% Margin */}
      <div style={{ width: '5%' }}></div>
    </div> 
    {/* Google Map */}
    <GoogleMap
      mapContainerStyle={containerStyle}
      {...(!isBoundsMode ? { center: mapCenter, zoom: 10 } : {})}
      onLoad={map => {
        mapRef.current = map;
        if (map.getZoom()) setCurrentZoom(map.getZoom());
        
        // Add mouse move listener to track when cursor is over route
        map.addListener('mousemove', (e) => {
          if (directionsResponse && directionsResponse.routes && directionsResponse.routes[0]) {
            const route = directionsResponse.routes[0];
            const mousePos = {
              lat: e.latLng.lat(),
              lng: e.latLng.lng()
            };
            
            // Check if mouse is near route with precise threshold for hover detection
            const threshold = 0.0005; // More precise threshold for hover detection (roughly 50 meters)
            let isNearRoute = false;
            
            for (let i = 0; i < route.overview_path.length; i++) {
              const pathPoint = route.overview_path[i];
              const pathLat = typeof pathPoint.lat === 'function' ? pathPoint.lat() : pathPoint.lat;
              const pathLng = typeof pathPoint.lng === 'function' ? pathPoint.lng() : pathPoint.lng;
              
              const distance = Math.sqrt(
                Math.pow(mousePos.lat - pathLat, 2) + 
                Math.pow(mousePos.lng - pathLng, 2)
              );
              
              if (distance < threshold) {
                isNearRoute = true;
                break;
              }
            }
            
            setIsHoveringRoute(isNearRoute);
            console.log('Mouse hover check - isNearRoute:', isNearRoute, 'at:', mousePos);
          } else {
            setIsHoveringRoute(false);
          }
        });
      }}
      onZoomChanged={() => {
        if (mapRef.current) {
          const zoom = mapRef.current.getZoom();
          if (zoom) setCurrentZoom(zoom);
        }
      }}
      onClick={(e) => { // click handler for map
        // First, close any existing route info window when clicking elsewhere
        setRouteInfoWindow(null);
        setRouteClickPosition(null);
        setRouteCleared(true); // Reset route cleared state
        
        const clickedLocation = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };
        
        // Check if click is near the route - simplified approach
        if (directionsResponse && directionsResponse.routes && directionsResponse.routes[0]) {
          const route = directionsResponse.routes[0];
          console.log('Checking route click at:', clickedLocation);
          
          // Use a more precise threshold for route detection
          const routeThreshold = 0.0003; // More precise threshold (roughly 30 meters)
          let isNearRoute = false;
          
          for (let i = 0; i < route.overview_path.length; i++) {
            const pathPoint = route.overview_path[i];
            const pathLat = typeof pathPoint.lat === 'function' ? pathPoint.lat() : pathPoint.lat;
            const pathLng = typeof pathPoint.lng === 'function' ? pathPoint.lng() : pathPoint.lng;
            
            const distance = Math.sqrt(
              Math.pow(clickedLocation.lat - pathLat, 2) + 
              Math.pow(clickedLocation.lng - pathLng, 2)
            );
            
            if (distance < routeThreshold) {
              isNearRoute = true;
              break;
            }
          }
          
          if (isNearRoute) {
            console.log('Route click detected via main click handler!');
            handleRouteClick(e);
            return; // Exit early if route was clicked
          }
        }
        
        e.stop(); // Prevent default behavior
        if (e.placeId) {
          // If a POI/business was clicked, use PlacesService to get details
          const service = new window.google.maps.places.PlacesService(document.createElement("div"));
          incrementPlacesApiCounter(); // Track API call
          service.getDetails({ placeId: e.placeId }, (place, serviceStatus) => {
            if (serviceStatus === window.google.maps.places.PlacesServiceStatus.OK) {
              setNewplace(place,clickedLocation,place.name);
                          setShouldAutoOpenInfoWindow(true); // Ensure InfoWindow opens
 }
              else {
              // Fallback to Geocoder if PlacesService fails
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: clickedLocation }, (results, status) => {
                if (status === "OK" && results[0]) {
                  const placeName = results[0].formatted_address || "Unknown Location";
                  setNewplace(results[0],clickedLocation,placeName);
                } else {
                  console.error("Geocoder failed due to: " + status);
                }
              });
            }
          });
        } else {
          // If not a POI, fallback to Geocoder
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: clickedLocation }, (results, status) => {
            if (status === "OK" && results[0]) {
              const placeId = results[0].place_id;
              if (placeId) {
                // Try PlacesService for more details
                const service = new window.google.maps.places.PlacesService(document.createElement("div"));
                incrementPlacesApiCounter(); // Track API call
                service.getDetails({ placeId: placeId }, (place, serviceStatus) => {
                  if (serviceStatus === window.google.maps.places.PlacesServiceStatus.OK) {
                    setNewplace(place,clickedLocation,place.name);
                    } else {
                    // Fallback to address
                    const placeName = results[0].formatted_address || "Unknown Location";
                    setNewplace(results[0],clickedLocation,placeName);
                    }
                });
              } else {
                // No placeId, just use address
                const placeName = results[0].formatted_address || "Unknown Location";
                setNewplace(results[0],clickedLocation,placeName);
                }
            } else {
              console.error("Geocoder failed due to: " + status);
            }
          });
        }
      }}
    >
      {/* Render directions if available */}
      {directionsResponse && (
        <DirectionsRenderer 
          directions={directionsResponse}
          options={{
            suppressMarkers: true, // Suppress default route markers to avoid default InfoWindow popup
            polylineOptions: {
              strokeColor: isHoveringRoute ? '#0d4d8c' : '#1557b0', // Darker blue when hovering
              strokeWeight: isHoveringRoute ? 8 : 6, // Thicker when hovering
              strokeOpacity: 0.8,
              clickable: false // Disable clicking on the main route line to avoid conflicts
            }
          }}
        />
      )}
      
      {/* Highlighted segment polyline */}
      {directionsResponse && directionsResponse.routes && directionsResponse.routes[0] && 
       selectedSegmentIndex >= 0 && directionsResponse.routes[0].legs[selectedSegmentIndex] && (
        <Polyline
          path={directionsResponse.routes[0].legs[selectedSegmentIndex].steps.flatMap(step => step.path)}
          options={{
            strokeColor: '#ff4444',
            strokeWeight: 8,
            strokeOpacity: 1,
            zIndex: 1000
          }}
        />
      )}
      
      {/* Invisible clickable overlay for route interaction - prioritize this over complex detection */}
      {directionsResponse && directionsResponse.routes && directionsResponse.routes[0] && (
        <Polyline
          path={directionsResponse.routes[0].overview_path}
          options={{
            strokeColor: 'transparent', // Invisible
            strokeWeight: 20, // Reduced from 30 to be more precise
            strokeOpacity: 0,
            zIndex: 1000, // Ensure it's on top
            clickable: true
          }}
          onClick={(e) => {
            console.log('Polyline overlay clicked directly!');
            handleRouteClick(e);
            e.stop(); // Prevent event propagation
          }}
        />
      )}
      
      {/* Filter and render trip markers */}
      {(() => {
        // Filter tripLocations based on selected day filters
        const filteredTripLocations = tripLocations && tripLocations.length > 0 
          ? tripLocations.filter(marker => {
              if (!marker) return false;
              
              // Always show the selected marker event, regardless of filter
              if (typeof selectedDayIdx === 'number' && 
                  typeof selectedEventIdx === 'number' &&
                  marker.dayIdx === selectedDayIdx && 
                  marker.eventIdx === selectedEventIdx) {
                return true;
              }
              
              // If "All Days" is selected, show all markers
              if (selectedDayFilters.has(ALL_DAYS_FILTER)) return true;
              
              // Otherwise, only show markers from selected days
              return selectedDayFilters.has(marker.dayIdx);
            })
          : [];
          
        return filteredTripLocations.map((marker, index) => {
          // Defensive: marker may be null
          if (
            editingEvent &&
            editingEvent.dayIdx === marker.dayIdx &&
            editingEvent.eventIdx === marker.eventIdx
          ) {
            return null; // Hide the original marker while editing
          }
          if (!marker) return null;

          // Skip rendering markers for route events (they don't need visual markers) - BUG , as if not in route mode we should draw them
          if (marker.isRoute) {
            return null;
          } 
          // Use eventID for robust selection
          const isSelected = marker.eventID && props.selectedEventID && marker.eventID === props.selectedEventID;
          
          // Check if this marker is part of multi-selection
          const isMultiSelected = isMultiSelectMode && selectedEvents && marker.eventID && selectedEvents.has(marker.eventID);
        
        // Highlight marker if it's either single-selected OR multi-selected
        const shouldHighlight = isSelected || isMultiSelected;
        const googleMaps = typeof window !== 'undefined' && window.google && window.google.maps
          ? window.google.maps
          : null;
        const highlightIcon = {
          url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
          scaledSize: googleMaps ? new googleMaps.Size(40, 40) : undefined
        };
        const eventTypeIcon = useEventIcons
          ? {
              url: getMarkerIcon(marker.eventType, { size: EVENT_MARKER_SIZE, highlight: shouldHighlight }),
              scaledSize: googleMaps ? new googleMaps.Size(EVENT_MARKER_SIZE, EVENT_MARKER_SIZE) : undefined,
              anchor: googleMaps ? new googleMaps.Point(EVENT_MARKER_SIZE / 2, EVENT_MARKER_SIZE / 2) : undefined
            }
          : undefined;
  const markerIcon = useEventIcons ? eventTypeIcon : (shouldHighlight ? highlightIcon : undefined);
        
        // For route events, don't show InfoWindow (they have no location details to display)
        const showInfoWindow = !marker.isRoute && (
          (isSelected && selectedPlace && selectedPlace.eventID && selectedPlace.eventID === marker.eventID) ||
          (selectedPlace && selectedPlace.dayIdx === marker.dayIdx && selectedPlace.eventIdx === marker.eventIdx && (!marker.eventID || selectedPlace.eventID !== marker.eventID))
        );
        
        // Show Add Location button only for temporary marker (not for selected event marker)
        const showAddLocation = !marker.eventID || (selectedPlace && selectedPlace.eventID !== marker.eventID);
        return (
          <Marker
            key={index}
            position={marker}
            icon={markerIcon}
              /* in case we want to display the event type icons
               icon={{
              url: getMarkerIcon(marker.eventType), // marker.eventType should match your event type string
              scaledSize: new window.google.maps.Size(28, 28), // adjust size as needed
            }} */
            onClick={() => {
              // Close route info window when clicking on location markers
              setRouteInfoWindow(null);
              setRouteClickPosition(null);
              
              // Handle regular location events
              setSelectedPlace({
                name: marker.name || location.name,
                geometry: { location: { lat: marker.lat, lng: marker.lng } },
                zoom: marker.zoom || 15,
                formatted_address: marker.formatted_address || marker.name || '' ,
                place_id: marker.place_id || null,
                website: marker.website  || null,
                url: marker.url || (marker.place_id ? `https://www.google.com/maps/place/?q=place_id:${marker.place_id}` : null),
                formatted_phone_number: marker.formatted_phone_number ||  null,
                dayIdx: marker.dayIdx,
                eventIdx: marker.eventIdx,
                rating: marker.rating || null,
                user_ratings_total: marker.user_ratings_total || null,
                weekday_text: marker.weekday_text || [],
                eventID: marker.eventID || null
              });
              if (props.setSelectedDayIdx && props.setSelectedEventIdx && marker.dayIdx != null && marker.eventIdx != null) {
                props.setSelectedDayIdx(marker.dayIdx);
                props.setSelectedEventIdx(marker.eventIdx);
              }
              if (props.setSelectedEventID && marker.eventID) {
                props.setSelectedEventID(marker.eventID);
              }
             
            }}
          >
            {showInfoWindow  && (
              <InfoWindow
                position={{ lat: marker.lat , lng: marker.lng }}
                onCloseClick={() => {
                  setSelectedPlace(null);
                  setShouldAutoOpenInfoWindow(false);
                }}
              >
                <div className="myMap-infowindow">
    <style>
      {`
        .myMap-infowindow {
          padding-top: 6px;
          padding-bottom: 8px;
          padding-left: 12px;
          padding-right: 12px;
          min-width: 220px;
          box-sizing: border-box;
          position: relative;
        }
        .myMap-infowindow-close-btn {
          position: absolute;
          top: 2px;
          right: 2px;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid #ccc;
          border-radius: 50%;
          font-size: 14px;
          font-weight: bold;
          color: #666;
          cursor: pointer;
          padding: 0;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          z-index: 1000;
        }
        .myMap-infowindow-close-btn:hover {
          color: #000;
          background-color: #fff;
          border-color: #999;
        }
        .myMap-infowindow-title {
          font-size: 1.1em;
          font-weight: bold;
          margin: 0;
          padding: 0 25px 0 0;
          flex: 1;
          word-break: break-word;
        }
        .myMap-infowindow-rating {
          margin: 0 0 5px;
          font-size: 1em;
          color: #444;
          display: flex;
          align-items: center;
        }
        .myMap-infowindow-stars {
          color: #fbbc04 !important;
          margin-right: 6px;
        }
        .myMap-infowindow-user-ratings {
          color: #888;
          font-size: 0.95em;
        }
        .myMap-infowindow-address {
          margin: 0 0 5px;
          font-size: 0.95em;
          color: #444;
        }
        .myMap-infowindow-link {
          color: #007bff;
          text-decoration: none;
          display: block;
          margin-bottom: 5px;
        }
        .myMap-infowindow-phone {
          margin: 0 0 5px;
          font-size: 0.9em;
          color: #666;
        }
        .myMap-infowindow-add-btn {
          background-color: #28a745 !important;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 3px;
          cursor: pointer;
          margin-top: 10px;
        }
          .gm-ui-hover-effect  {
  display: none !important;
}
      `}
    </style>
    <button 
      className="myMap-infowindow-close-btn"
      onClick={() => {
        setSelectedPlace(null);
        setShouldAutoOpenInfoWindow(false);
      }}
      title="Close"
    >
      ×
    </button>
    <h3 className="myMap-infowindow-title">
      {selectedPlace.name || selectedPlace.formatted_address}
    </h3>
    {selectedPlace.rating && (
      <div className="myMap-infowindow-rating">
        Rating: 
        <span style={{ marginRight: 6 , paddingLeft : 5}}>{selectedPlace.rating}</span>
        <span className="myMap-infowindow-stars">
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i}>
              {selectedPlace.rating >= i + 1
                ? '★'
                : selectedPlace.rating >= i + 0.5
                  ? '☆'
                  : '☆'}
            </span>
          ))}
        </span>
        {selectedPlace.user_ratings_total && (
          <span className="myMap-infowindow-user-ratings">
            ({selectedPlace.user_ratings_total})
          </span>
        )}
      </div>
    )}
    {selectedPlace.formatted_address && (
      <p className="myMap-infowindow-address">{selectedPlace.formatted_address}</p>
    )}
  {/* ...existing code... */}

    {(selectedPlace.url || selectedPlace.place_id) && (
      <a
        href={selectedPlace.url || `https://www.google.com/maps/place/?q=place_id:${selectedPlace.place_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="myMap-infowindow-link"
      >
        View on Google Maps
      </a>
    )}
    {selectedPlace.website && (
      <a
        href={selectedPlace.website}
        target="_blank"
        rel="noopener noreferrer"
        className="myMap-infowindow-link"
      >
        Business Website
      </a>
    )}
    {selectedPlace.formatted_phone_number && (
      <p className="myMap-infowindow-phone">
        Phone: {selectedPlace.formatted_phone_number}
      </p>
    )}
    {showAddLocation && isOwner && (
      <button
        onClick={() => {
          if (selectedPlace) {
            handlesetLocation();
            setSelectedPlace(null); // <-- This will close the InfoWindow
            setShouldAutoOpenInfoWindow(false);
          }
        }}
        className="myMap-infowindow-add-btn"
      >
        Add Location
      </button>
    )}
    {/* Blue Navigate button at the very end of InfoWindow */}
    {(() => {
      let lat = selectedPlace.lat;
      let lng = selectedPlace.lng;
      if ((lat === undefined || lat === null) && selectedPlace.geometry?.location) {
        lat = typeof selectedPlace.geometry.location.lat === 'function' ? selectedPlace.geometry.location.lat() : selectedPlace.geometry.location.lat;
      }
      if ((lng === undefined || lng === null) && selectedPlace.geometry?.location) {
        lng = typeof selectedPlace.geometry.location.lng === 'function' ? selectedPlace.geometry.location.lng() : selectedPlace.geometry.location.lng;
      }
      if (lat !== undefined && lng !== undefined && isFinite(lat) && isFinite(lng)) {
        return (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${lat}%2C${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              width: '100%',
              backgroundColor: '#007bff',
              color: 'white',
              textAlign: 'center',
              padding: '10px 0',
              borderRadius: '4px',
              fontWeight: 'bold',
              textDecoration: 'none',
              marginTop: '12px',
              fontSize: '1em',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}
          >
            Navigate
          </a>
        );
      }
      return null;
    })()}
  </div>
              </InfoWindow> 
            ) }
          </Marker>
      ); // <-- Ensure this closes the map function's return
      });
      })()}
      
      {/* Render a temporary marker for selectedPlace if it is not already in tripLocations */}
      {selectedPlace &&
        selectedPlace.geometry &&
        selectedPlace.geometry.location &&
        (() => {
          const loc = selectedPlace.geometry.location;
          const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          if (
            typeof lat === 'number' && typeof lng === 'number' &&
            isFinite(lat) && isFinite(lng) &&
            !tripLocations.some(m => m.lat === lat && m.lng === lng)
          ) {
            return (
              <Marker
                position={{ lat, lng }}
                onClick={() => {
                  // Close route info window when clicking on temporary markers
                  setRouteInfoWindow(null);
                  setRouteClickPosition(null);
                  setSelectedPlace(selectedPlace);
                }}
              >
                <InfoWindow
                  position={{ lat: lat + 0.001, lng }} // Offset a few pixels up
                  onCloseClick={() => {
                    setSelectedPlace(null);
                    setShouldAutoOpenInfoWindow(false);
                  }}
                >
                  <div className="myMap-infowindow">
                        <style>
      {`
        .myMap-infowindow {
          padding-top: 6px;
          padding-bottom: 8px;
          padding-left: 12px;
          padding-right: 12px;
          min-width: 220px;
          box-sizing: border-box;
          position: relative;
        }
        .myMap-infowindow-close-btn {
          position: absolute;
          top: 2px;
          right: 2px;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid #ccc;
          border-radius: 50%;
          font-size: 14px;
          font-weight: bold;
          color: #666;
          cursor: pointer;
          padding: 0;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          z-index: 1000;
        }
        .myMap-infowindow-close-btn:hover {
          color: #000;
          background-color: #fff;
          border-color: #999;
        }
        .myMap-infowindow-title {
          font-size: 1.1em;
          font-weight: bold;
          margin: 0;
          padding: 0 25px 0 0;
          flex: 1;
          word-break: break-word;
        }
        .myMap-infowindow-rating {
          margin: 0 0 5px;
          font-size: 1em;
          color: #444;
          display: flex;
          align-items: center;
        }
        .myMap-infowindow-stars {
          color: #fbbc04 !important;
          margin-right: 6px;
        }
        .myMap-infowindow-user-ratings {
          color: #888;
          font-size: 0.95em;
        }
        .myMap-infowindow-address {
          margin: 0 0 5px;
          font-size: 0.95em;
          color: #444;
        }
        .myMap-infowindow-link {
          color: #007bff;
          text-decoration: none;
          display: block;
          margin-bottom: 5px;
        }
        .myMap-infowindow-phone {
          margin: 0 0 5px;
          font-size: 0.9em;
          color: #666;
        }
        .myMap-infowindow-add-btn {
          background-color: #28a745 !important;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 3px;
          cursor: pointer;
          margin-top: 10px;
        }
          .gm-ui-hover-effect  {
  display: none !important;
}

      `}
    </style>
    <button 
      className="myMap-infowindow-close-btn"
      onClick={() => {
        setSelectedPlace(null);
        setShouldAutoOpenInfoWindow(false);
      }}
      title="Close"
    >
      ×
    </button>
    <h3 className="myMap-infowindow-title">
      {selectedPlace.name || selectedPlace.formatted_address}
    </h3>
    {selectedPlace.rating && (
      <div className="myMap-infowindow-rating">
        Rating: 
        <span style={{ marginRight: 6 , paddingLeft : 5}}>{selectedPlace.rating}</span>
        <span className="myMap-infowindow-stars">
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i}>
              {selectedPlace.rating >= i + 1
                ? '★'
                : selectedPlace.rating >= i + 0.5
                  ? '☆'
                  : '☆'}
            </span>
          ))}
        </span>
        {selectedPlace.user_ratings_total && (
          <span className="myMap-infowindow-user-ratings">
            ({selectedPlace.user_ratings_total})
          </span>
        )}
      </div>
    )}
    {selectedPlace.formatted_address && (
      <p className="myMap-infowindow-address">{selectedPlace.formatted_address}</p>
    )}
  {/* ...existing code... */}
    {(selectedPlace.url || selectedPlace.place_id) && (
      <a
        href={selectedPlace.url || `https://www.google.com/maps/place/?q=place_id:${selectedPlace.place_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="myMap-infowindow-link"
      >
        View on Google Maps
      </a>
    )}
    {selectedPlace.website && (
      <a
        href={selectedPlace.website}
        target="_blank"
        rel="noopener noreferrer"
        className="myMap-infowindow-link"
      >
        Business Website
      </a>
    )}
    {selectedPlace.formatted_phone_number && (
      <p className="myMap-infowindow-phone">
        Phone: {selectedPlace.formatted_phone_number}
      </p>
    )}
    {/* Blue Navigate button at the bottom */}
    {(() => {
      let lat = selectedPlace.lat;
      let lng = selectedPlace.lng;
      if ((lat === undefined || lat === null) && selectedPlace.geometry?.location) {
        lat = typeof selectedPlace.geometry.location.lat === 'function' ? selectedPlace.geometry.location.lat() : selectedPlace.geometry.location.lat;
      }
      if ((lng === undefined || lng === null) && selectedPlace.geometry?.location) {
        lng = typeof selectedPlace.geometry.location.lng === 'function' ? selectedPlace.geometry.location.lng() : selectedPlace.geometry.location.lng;
      }
      if (lat !== undefined && lng !== undefined && isFinite(lat) && isFinite(lng)) {
        return (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${lat}%2C${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              width: '100%',
              backgroundColor: '#007bff',
              color: 'white',
              textAlign: 'center',
              padding: '10px 0',
              borderRadius: '4px',
              fontWeight: 'bold',
              textDecoration: 'none',
              marginTop: '12px',
              fontSize: '1em',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}
          >
            Navigate
          </a>
        );
      }
      return null;
    })()}
   {isOwner && (
     <button
       onClick={() => {
         if (selectedPlace) {
           handlesetLocation();
         //  setSelectedPlace(null); // <-- This will close the InfoWindow
           setShouldAutoOpenInfoWindow(false);
        }
      }}
            className="myMap-infowindow-add-btn"
    >
      Add Location
    </button> )}
  </div>
                </InfoWindow>
              </Marker>
            );
          } else {
            if (selectedPlace && (lat == null || lng == null || !isFinite(lat) || !isFinite(lng))) {
              console.warn('Skipping temporary marker for selectedPlace due to invalid lat/lng:', lat, lng, selectedPlace);
            }
          }
          return null;
        })()}
      {/* Editable marker for the currently editing event */}
      {editingEvent && editingEvent.event && editingEvent.event.location && (
  <Marker
    position={{
      lat: editingEvent.event.location.lat,
      lng: editingEvent.event.location.lng
    }}
    icon={{
      url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
      scaledSize: new window.google.maps.Size(40, 40)
    }}
  />
)}
    </GoogleMap>
    
    {/* Route InfoWindow - positioned outside GoogleMap as custom dialog */}
    <RouteInfoWindow 
      routeInfo={routeInfoWindow}
      position={routeClickPosition}
      onClose={() => {
        setRouteInfoWindow(null);
        setRouteClickPosition(null);
        setSelectedSegmentIndex(-1); // Clear selected segment when closing
      }}
      onTravelModeChange={handleTravelModeChange}
      onSegmentSelect={handleSegmentSelect}
      selectedSegmentIndex={selectedSegmentIndex}
      mapRef={mapRef}
    />
  </div>
    </LoadScript>
    
  );
};


export default MyMap;
